import type { InstrumentPanel, InstrumentSettings } from 'lib-analytics-types';
import { useCallback, useMemo } from 'react';
import { useMap, useUpdateEffect } from 'react-use';

export type UsePersistInstrumentSettingsResult = {
  /** The persisted instrument settings. */
  instrumentSettings?: InstrumentSettings;
  /** The method to set a specific instrument setting. */
  setInstrumentSetting: <TKey extends keyof InstrumentSettings>(
    key: TKey,
    value: InstrumentSettings[TKey],
  ) => void;
};

/**
 * A hook that handles persisting instrument settings to be included in the API
 * request, that are not persisted in the global settings state.
 *
 * These settings are scoped and applied on each individual panel, while the
 * global settings are applied to all panels in a dashboard, and they are
 * typically instrument-specific settings such as chart granularity, which when
 * changed trigger a new API request.
 */
export const usePersistInstrumentSettings = (
  panel?: InstrumentPanel,
): UsePersistInstrumentSettingsResult => {
  const getInstrumentSettingsFromControls =
    useCallback((): InstrumentSettings => {
      if (!panel?.controls) return {};

      const { granularity, visualization } = panel.controls.settings;
      return { granularity, visualization };
    }, [panel?.controls]);

  const [instrumentSettings, { set, setAll }] = useMap<InstrumentSettings>(
    getInstrumentSettingsFromControls(),
  );

  // Updates the local instrument settings when the panel controls change.
  useUpdateEffect(() => {
    setAll(getInstrumentSettingsFromControls());
  }, [panel?.controls]);

  return useMemo(
    () => ({ instrumentSettings, setInstrumentSetting: set }),
    [instrumentSettings, set],
  );
};
